import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CredentialProbeResult } from '../../types/paymentProcessor';

interface CheckoutChampCredentialCheckDialogProps {
  open: boolean;
  accountName: string;
  onClose: () => void;
  onCheck: () => Promise<CredentialProbeResult[]>;
}

export default function CheckoutChampCredentialCheckDialog({
  open,
  accountName,
  onClose,
  onCheck,
}: CheckoutChampCredentialCheckDialogProps) {
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<CredentialProbeResult[] | null>(null);

  const runCheck = async () => {
    setChecking(true);
    setError(null);
    try {
      const outcome = await onCheck();
      setResults(outcome);
    } catch (err: any) {
      setError(err.message || 'Failed to check credentials');
      setResults(null);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (open) {
      setResults(null);
      setError(null);
      runCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const authFailed = results?.some((r) => r.outcome === 'auth_failed') ?? false;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <DialogTitle>Verify credentials — {accountName}</DialogTitle>
      <DialogContent>
        {checking && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">Checking...</Typography>
          </Box>
        )}

        {!checking && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!checking && !error && authFailed && (
          <Alert severity="error">
            Could not authenticate with Checkout Champ. Check the Login ID and Password and try again.
          </Alert>
        )}

        {!checking && !error && results && !authFailed && (
          <List dense disablePadding>
            {results.map((result) => (
              <ListItem key={result.label} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {result.outcome === 'ok' ? (
                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={result.label}
                  secondary={
                    result.outcome === 'permission_denied' ? 'missing permission'
                      : result.outcome === 'unreachable' ? 'not reachable'
                        : undefined
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={runCheck} disabled={checking}>Recheck</Button>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
